import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { createInvoice, updateInvoice, getNextInvoiceNumber, getLatestDraft } from '../../services/invoices';
import { sendEstimateEmail, sendInvoiceEmail } from '../../services/email';
import { createAuditLog } from '../../services/audit';
import { getDefaultTaxRate, getEnableDueDates } from '../../services/settings';
import { getLetterTemplate, getDefaultLetterTemplates } from '../../services/letterTemplates';
import { renderTemplate } from '../../utils/templateRenderer';
import { assembleDocument } from '../../utils/assembleDocument';
import './GenerateInvoiceTab.css';

export default function GenerateInvoiceTab({ client }) {
  const { user } = useAuth();
  const [invoiceData, setInvoiceData] = useState({
    invoice_number: '',
    type: 'estimate', // 'estimate' or 'invoice'
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    items: [{ description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }],
    subtotal: 0,
    taxable_subtotal: 0,
    non_taxable_subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 0,
    notes: '',
    status: 'draft',
    hide_totals: false, // New field to hide totals for option selection
    include_tax: true,  // When false, total = subtotal (no tax applied)
  });
  const [draftId, setDraftId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [loadingInvoiceNumber, setLoadingInvoiceNumber] = useState(true);
  const [showPrintView, setShowPrintView] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const autoSaveTimeoutRef = useRef(null);
  const hasChangesRef = useRef(false);
  const [letterTemplate, setLetterTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [enableDueDates, setEnableDueDates] = useState(true);

  // Load default tax rate and due date settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      const defaultRate = await getDefaultTaxRate();
      const enableDueDatesSetting = await getEnableDueDates();
      setEnableDueDates(enableDueDatesSetting);
      
      setInvoiceData(prev => ({
        ...prev,
        tax_rate: prev.tax_rate || defaultRate,
        // Auto-fill due date if enabled
        due_date: enableDueDatesSetting && !prev.due_date 
          ? new Date().toISOString().split('T')[0] 
          : prev.due_date
      }));
    };
    loadSettings();
  }, []);

  // Load letter template for print view
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setLoadingTemplate(true);
        const templateType = invoiceData.type === 'estimate' ? 'estimate' : 'invoice';
        const template = await getLetterTemplate(templateType);
        setLetterTemplate(template);
      } catch (error) {
        console.log('Using default letter template:', error);
        const defaults = getDefaultLetterTemplates();
        setLetterTemplate(defaults[invoiceData.type === 'estimate' ? 'estimate' : 'invoice']);
      } finally {
        setLoadingTemplate(false);
      }
    };
    loadTemplate();
  }, [invoiceData.type]);

  // Load letter template for print view
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setLoadingTemplate(true);
        const templateType = invoiceData.type === 'estimate' ? 'estimate' : 'invoice';
        const template = await getLetterTemplate(templateType);
        setLetterTemplate(template);
      } catch (error) {
        console.log('Using default letter template:', error);
        const defaults = getDefaultLetterTemplates();
        setLetterTemplate(defaults[invoiceData.type === 'estimate' ? 'estimate' : 'invoice']);
      } finally {
        setLoadingTemplate(false);
      }
    };
    loadTemplate();
  }, [invoiceData.type]);

  // Open print dialog only after template has loaded and content has rendered
  useEffect(() => {
    if (!showPrintView || !letterTemplate || loadingTemplate) return;
    const timeoutId = setTimeout(() => window.print(), 150);
    const onAfterPrint = () => {
      setShowPrintView(false);
      window.removeEventListener('afterprint', onAfterPrint);
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [showPrintView, letterTemplate, loadingTemplate]);

  // Initialize tax rate when creating new invoice (not from draft)
  useEffect(() => {
    const initializeTaxRate = async () => {
      if (!draftId && invoiceData.tax_rate === 0) {
        const defaultRate = await getDefaultTaxRate();
        setInvoiceData(prev => ({
          ...prev,
          tax_rate: defaultRate
        }));
      }
    };
    initializeTaxRate();
  }, [draftId, invoiceData.tax_rate]);

  // Load draft or initialize new invoice on component mount
  useEffect(() => {
    const loadDraftOrInitialize = async () => {
      try {
        setLoadingInvoiceNumber(true);
        
        // Get default tax rate
        const defaultTaxRate = await getDefaultTaxRate();
        
        // Check for existing draft (only active drafts, not finalized)
        const draft = await getLatestDraft(client.id);
        
        if (draft && draft.status === 'draft' && draft.type === 'estimate') {
          // Only load if it's truly a draft estimate (not finalized)
          // Check if draft was updated recently (within last 7 days) to avoid loading very old drafts
          const draftDate = new Date(draft.updated_at || draft.created_at);
          const daysSinceUpdate = (new Date() - draftDate) / (1000 * 60 * 60 * 24);
          
          if (daysSinceUpdate <= 7) {
            // Load existing draft
            setDraftId(draft.id);
            
            // Parse items if they're stored as JSON string
            let items = [{ description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }];
            if (draft.items) {
              try {
                items = typeof draft.items === 'string' ? JSON.parse(draft.items) : draft.items;
                if (!Array.isArray(items) || items.length === 0) {
                  items = [{ description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }];
                }
              } catch (e) {
                console.error('Error parsing draft items:', e);
                items = [{ description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }];
              }
            }
            
            setInvoiceData({
              invoice_number: draft.invoice_number || '',
              type: draft.type || 'estimate',
              date: draft.date || new Date().toISOString().split('T')[0],
              due_date: draft.due_date || '',
              items: items,
              subtotal: draft.subtotal || 0,
              taxable_subtotal: draft.taxable_subtotal || 0,
              non_taxable_subtotal: draft.non_taxable_subtotal || 0,
              tax_rate: draft.tax_rate || defaultTaxRate,
              tax_amount: draft.tax_amount || 0,
              total: draft.total || 0,
              notes: draft.notes || '',
              status: 'draft',
              hide_totals: draft.hide_totals || false,
              include_tax: draft.include_tax !== false,
            });
            setAutoSaveStatus('Draft loaded');
            setTimeout(() => setAutoSaveStatus(''), 3000);
          } else {
            // Draft is too old, start fresh
            const nextNumber = await getNextInvoiceNumber();
            setInvoiceData(prev => ({ ...prev, invoice_number: nextNumber }));
          }
        } else {
          // No draft found, initialize new invoice
          const nextNumber = await getNextInvoiceNumber();
          setInvoiceData(prev => ({ ...prev, invoice_number: nextNumber }));
        }
      } catch (error) {
        console.error('Error loading draft or invoice number:', error);
        // Fallback to current year starting at 0001
        const currentYear = new Date().getFullYear();
        const yearSuffix = currentYear.toString().slice(-2);
        setInvoiceData(prev => ({ ...prev, invoice_number: `${yearSuffix}-0001` }));
      } finally {
        setLoadingInvoiceNumber(false);
      }
    };
    
    if (client?.id) {
      loadDraftOrInitialize();
    }
  }, [client?.id]);

  // Auto-save function
  const autoSave = useCallback(async (data) => {
    if (!client?.id || !user?.id) return;

    try {
      const invoiceToSave = {
        client_id: client.id,
        invoice_number: data.invoice_number,
        type: data.type,
        date: data.date,
        due_date: data.due_date || null,
        items: JSON.stringify(data.items),
        subtotal: parseFloat(data.subtotal) || 0,
        taxable_subtotal: parseFloat(data.taxable_subtotal) || 0,
        non_taxable_subtotal: parseFloat(data.non_taxable_subtotal) || 0,
        tax_rate: parseFloat(data.tax_rate) || 0,
        tax_amount: parseFloat(data.tax_amount) || 0,
        total: parseFloat(data.total) || 0,
        notes: data.notes || '',
        status: 'draft',
        created_by: user.id,
        include_tax: data.include_tax !== false,
      };

      if (draftId) {
        // Update existing draft
        await updateInvoice(draftId, invoiceToSave);
      } else {
        // Create new draft
        const newDraft = await createInvoice(invoiceToSave);
        setDraftId(newDraft.id);
      }
      
      setAutoSaveStatus('Auto-saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
      hasChangesRef.current = false;
    } catch (error) {
      console.error('Error auto-saving:', error);
      // Don't show error to user for auto-save failures
    }
  }, [client?.id, user?.id, draftId]);

  // Debounced auto-save
  useEffect(() => {
    if (hasChangesRef.current && !loadingInvoiceNumber) {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout for auto-save (3 seconds after last change)
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSave(invoiceData);
      }, 3000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [invoiceData, autoSave, loadingInvoiceNumber]);

  const calculateItemTotal = (quantity, unitPrice) => {
    return (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);
  };

  const updateItem = (index, field, value) => {
    hasChangesRef.current = true;
    setInvoiceData(prev => {
      const newItems = [...prev.items];
      const updatedItem = { ...newItems[index], [field]: value };
      
      if (field === 'quantity' || field === 'unit_price') {
        updatedItem.total = calculateItemTotal(
          updatedItem.quantity,
          updatedItem.unit_price
        );
      }

      newItems[index] = updatedItem;
      
      // Calculate subtotals (taxable vs non-taxable) - exclude separators
      const taxableSubtotal = newItems
        .filter(item => !item.isSeparator && item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const nonTaxableSubtotal = newItems
        .filter(item => !item.isSeparator && !item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const totalSubtotal = taxableSubtotal + nonTaxableSubtotal;
      
      // Calculate tax only on taxable items (when include_tax is true)
      const taxAmount = prev.include_tax !== false ? taxableSubtotal * (parseFloat(prev.tax_rate) || 0) / 100 : 0;
      const total = totalSubtotal + taxAmount;
      
      return {
        ...prev,
        items: newItems,
        subtotal: totalSubtotal.toFixed(2),
        taxable_subtotal: taxableSubtotal.toFixed(2),
        non_taxable_subtotal: nonTaxableSubtotal.toFixed(2),
        tax_amount: taxAmount.toFixed(2),
        total: total.toFixed(2),
      };
    });
  };

  const addItem = () => {
    hasChangesRef.current = true;
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }],
    }));
  };

  const addSeparator = (index) => {
    hasChangesRef.current = true;
    setInvoiceData(prev => {
      const newItems = [...prev.items];
      newItems.splice(index + 1, 0, { isSeparator: true, showSubtotal: false, description: '', quantity: 0, unit_price: 0, taxable: false, total: 0 });
      return { ...prev, items: newItems };
    });
  };

  const toggleSeparatorSubtotal = (index) => {
    hasChangesRef.current = true;
    setInvoiceData(prev => {
      const newItems = [...prev.items];
      if (newItems[index]?.isSeparator) {
        newItems[index] = { ...newItems[index], showSubtotal: !newItems[index].showSubtotal };
      }
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index) => {
    hasChangesRef.current = true;
    setInvoiceData(prev => {
      const newItems = prev.items.filter((_, i) => i !== index);
      
      const taxableSubtotal = newItems
        .filter(item => !item.isSeparator && item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const nonTaxableSubtotal = newItems
        .filter(item => !item.isSeparator && !item.taxable)
        .reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
      
      const totalSubtotal = taxableSubtotal + nonTaxableSubtotal;
      const taxAmount = prev.include_tax !== false ? taxableSubtotal * (parseFloat(prev.tax_rate) || 0) / 100 : 0;
      const total = totalSubtotal + taxAmount;
      
      return {
        ...prev,
        items: newItems,
        subtotal: totalSubtotal.toFixed(2),
        taxable_subtotal: taxableSubtotal.toFixed(2),
        non_taxable_subtotal: nonTaxableSubtotal.toFixed(2),
        tax_amount: taxAmount.toFixed(2),
        total: total.toFixed(2),
      };
    });
  };

  const handleTaxRateChange = (e) => {
    hasChangesRef.current = true;
    const taxRate = parseFloat(e.target.value) || 0;
    setInvoiceData(prev => {
      const taxableSubtotal = parseFloat(prev.taxable_subtotal) || 0;
      const nonTaxableSubtotal = parseFloat(prev.non_taxable_subtotal) || 0;
      const totalSubtotal = taxableSubtotal + nonTaxableSubtotal;
      const taxAmount = prev.include_tax !== false ? taxableSubtotal * taxRate / 100 : 0;
      const total = totalSubtotal + taxAmount;
      
      return {
        ...prev,
        tax_rate: taxRate,
        tax_amount: taxAmount.toFixed(2),
        total: total.toFixed(2),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');

    try {
      // Auto-fill due date if enabled and not set
      let dueDate = invoiceData.due_date;
      if (enableDueDates && !dueDate && invoiceData.type === 'invoice') {
        dueDate = new Date().toISOString().split('T')[0];
      }
      
      const invoiceToSave = {
        ...invoiceData,
        client_id: client.id,
        items: JSON.stringify(invoiceData.items),
        subtotal: parseFloat(invoiceData.subtotal),
        taxable_subtotal: parseFloat(invoiceData.taxable_subtotal || 0),
        non_taxable_subtotal: parseFloat(invoiceData.non_taxable_subtotal || 0),
        tax_rate: parseFloat(invoiceData.tax_rate),
        tax_amount: parseFloat(invoiceData.tax_amount),
        hide_totals: invoiceData.hide_totals || false,
        include_tax: invoiceData.include_tax !== false,
        total: parseFloat(invoiceData.total),
        created_by: user?.id || null,
        type: invoiceData.type === 'quote' ? 'estimate' : invoiceData.type, // Ensure type is 'estimate' or 'invoice'
        status: invoiceData.type === 'estimate' || invoiceData.type === 'quote' ? 'draft' : 'unpaid',
        due_date: enableDueDates ? dueDate : null, // Only set due_date if enabled
      };

      let savedInvoice;
      if (draftId) {
        // Update existing draft
        savedInvoice = await updateInvoice(draftId, invoiceToSave);
        await createAuditLog(
          'update',
          invoiceData.type === 'estimate' ? 'estimate' : 'invoice',
          savedInvoice.id,
          { 
            invoice_number: invoiceData.invoice_number,
            client_name: client.name,
            total: invoiceData.total 
          }
        );
      } else {
        // Create new invoice
        savedInvoice = await createInvoice(invoiceToSave);
        await createAuditLog(
          'create',
          invoiceData.type === 'estimate' ? 'estimate' : 'invoice',
          savedInvoice.id,
          { 
            invoice_number: invoiceData.invoice_number,
            client_name: client.name,
            total: invoiceData.total 
          }
        );
      }
      
      setSuccess(`${invoiceData.type === 'quote' ? 'Quote' : 'Invoice'} ${draftId ? 'updated' : 'created'} successfully!`);
      setDraftId(savedInvoice.id);
      hasChangesRef.current = false;
      
      // Get next invoice number and default tax rate for a new invoice/estimate
      const nextNumber = await getNextInvoiceNumber();
      const defaultTaxRate = await getDefaultTaxRate();
      
      // Reset form with new invoice number (but keep draft if it's an estimate)
      if (invoiceData.type === 'estimate') {
        // For estimates, keep the draft but prepare for a new one
        setInvoiceData(prev => ({
          ...prev,
          invoice_number: nextNumber,
          date: new Date().toISOString().split('T')[0],
          due_date: '',
          items: [{ description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }],
          subtotal: 0,
          taxable_subtotal: 0,
          non_taxable_subtotal: 0,
          tax_rate: defaultTaxRate,
          tax_amount: 0,
          total: 0,
          notes: '',
          status: 'draft',
        }));
        setDraftId(null);
      } else {
        // For invoices, reset completely
        setInvoiceData(prev => ({
          ...prev,
          invoice_number: nextNumber,
          date: new Date().toISOString().split('T')[0],
          due_date: '',
          items: [{ description: '', quantity: 1, unit_price: 0, taxable: true, total: 0 }],
          subtotal: 0,
          taxable_subtotal: 0,
          non_taxable_subtotal: 0,
          tax_rate: defaultTaxRate,
          tax_amount: 0,
          total: 0,
          notes: '',
          status: 'draft',
        }));
        setDraftId(null);
      }
    } catch (error) {
      alert('Failed to create ' + (invoiceData.type === 'quote' ? 'quote' : 'invoice') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    setShowPrintView(true);
  };

  const handleEmail = async () => {
    try {
      if (!client.email) {
        alert('Client email is required to send email');
        return;
      }

      // Show info message
      const docType = invoiceData.type === 'estimate' ? 'estimate' : 'receipt';
      alert(`A PDF ${docType} will be downloaded. After it downloads, your email client will open. Please attach the downloaded PDF file before sending the email.`);

      if (invoiceData.type === 'estimate') {
        await sendEstimateEmail(invoiceData, client);
      } else {
        await sendInvoiceEmail(invoiceData, client);
      }
    } catch (error) {
      alert('Failed to send email: ' + error.message);
    }
  };

  // Prepare template data for unified renderTemplate function
  const prepareTemplateData = () => {
    const items = typeof invoiceData.items === 'string' ? JSON.parse(invoiceData.items) : invoiceData.items;
    const clientCityStateZip = [client.city, client.state, client.zip].filter(Boolean).join(', ') || '';
    
    return {
      document_type: invoiceData.type === 'estimate' ? 'ESTIMATE' : 'INVOICE',
      client: {
        name: client.name || 'Client',
        address: client.address || '',
        city_state_zip: clientCityStateZip,
        email: client.email || '',
        phone: client.phone || '',
      },
      invoice: {
        number: invoiceData.invoice_number || '',
        date: invoiceData.date ? new Date(invoiceData.date).toLocaleDateString() : '',
        due_date: invoiceData.due_date ? new Date(invoiceData.due_date).toLocaleDateString() : '',
        document_type: invoiceData.type === 'estimate' ? 'ESTIMATE' : 'INVOICE',
        subtotal: `$${parseFloat(invoiceData.subtotal || 0).toFixed(2)}`,
        tax: `$${parseFloat(invoiceData.tax_amount || 0).toFixed(2)}`,
        tax_amount: `$${parseFloat(invoiceData.tax_amount || 0).toFixed(2)}`,
        total: `$${parseFloat(invoiceData.total || 0).toFixed(2)}`,
        tax_rate: invoiceData.tax_rate || 0,
        taxable_subtotal: invoiceData.taxable_subtotal || 0,
        non_taxable_subtotal: invoiceData.non_taxable_subtotal || 0,
        line_items: items,
        items_table: items,
        notes: invoiceData.notes || '',
      },
      date: invoiceData.date ? new Date(invoiceData.date).toLocaleDateString() : '',
      due_date: invoiceData.due_date ? new Date(invoiceData.due_date).toLocaleDateString() : '',
      facility: {
        name: 'Menlo Atherton Glass',
        address: 'PO Box 5214, Redwood City CA 94063',
      },
      user: {
        name: user?.user_metadata?.full_name || user?.email || '',
      },
      notes_section: invoiceData.notes || '',
      tax_rate: invoiceData.tax_rate || 0,
    };
  };

  // Print view component - uses template from Settings
  const PrintView = () => {
    if (loadingTemplate || !letterTemplate) {
      return <div className="print-view">Loading template...</div>;
    }
    
    // Prepare template data
    const templateData = prepareTemplateData();
    
    // UNIFIED PIPELINE: Assemble document then render
    // Step 1: Assemble document structure
    const assembledHTML = assembleDocument({
      header: letterTemplate.header || '',
      body: letterTemplate.body || '',
      footer: letterTemplate.footer || ''
    });
    
    // Step 2: Render with real data using unified renderTemplate
    const templateContent = renderTemplate(assembledHTML, templateData, invoiceData.hide_totals);
    
    return (
      <div className="print-view">
        <div 
          className="print-template-content"
          dangerouslySetInnerHTML={{ __html: templateContent }}
        />
      </div>
    );
  };

  if (showPrintView) {
    return createPortal(<PrintView />, document.body);
  }

  return (
    <div className="generate-invoice-tab">
      <div className="tab-header">
        <h2>Generate {invoiceData.type === 'quote' ? 'Quote' : 'Invoice'}</h2>
        {autoSaveStatus && (
          <div className={`auto-save-status ${autoSaveStatus.includes('saved') || autoSaveStatus.includes('loaded') ? 'saved' : ''}`}>
            {autoSaveStatus}
          </div>
        )}
        <div className="header-actions">
          <select
            value={invoiceData.type}
            onChange={(e) => {
              hasChangesRef.current = true;
              setInvoiceData(prev => ({ ...prev, type: e.target.value }));
            }}
            className="type-select"
          >
            <option value="estimate">Estimate</option>
            <option value="invoice">Invoice</option>
          </select>
        </div>
      </div>

      {success && (
        <div className="success-message">{success}</div>
      )}

      <form onSubmit={handleSubmit} className="invoice-form">
        <div className="form-section">
          <div className="form-row">
            <div className="form-group">
              <label>Document Number</label>
              <input
                type="text"
                value={invoiceData.invoice_number}
                readOnly
                disabled={loadingInvoiceNumber}
                className="invoice-number-input"
                placeholder={loadingInvoiceNumber ? "Loading..." : "26-0001"}
              />
              {loadingInvoiceNumber && (
                <small className="loading-text">Generating document number...</small>
              )}
            </div>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                value={invoiceData.date}
                onChange={(e) => {
                  hasChangesRef.current = true;
                  setInvoiceData(prev => ({ ...prev, date: e.target.value }));
                }}
                required
              />
            </div>
            {enableDueDates && (
              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  value={invoiceData.due_date}
                  onChange={(e) => {
                    hasChangesRef.current = true;
                    setInvoiceData(prev => ({ ...prev, due_date: e.target.value }));
                  }}
                />
              </div>
            )}
          </div>

          <div className="invoice-items-section">
            <div className="section-header">
              <h3>Items</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" className="btn-add-item" onClick={addItem}>
                  + Add Item
                </button>
                <button type="button" className="btn-add-separator" onClick={() => addSeparator(invoiceData.items.length - 1)}>
                  + Add Separator
                </button>
              </div>
            </div>

            <div className="items-table">
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Taxable</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceData.items.map((item, index) => {
                    if (item.isSeparator) {
                      const prevSepIndex = invoiceData.items.slice(0, index).findLastIndex((x) => x.isSeparator);
                      const sectionStart = prevSepIndex >= 0 ? prevSepIndex + 1 : 0;
                      const sectionItems = invoiceData.items.slice(sectionStart, index).filter((i) => !i.isSeparator);
                      const sectionSum = sectionItems.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                      const sectionTaxableSum = sectionItems
                        .filter((i) => i.taxable !== false)
                        .reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
                      const includeTax = invoiceData.include_tax !== false;
                      const taxRate = parseFloat(invoiceData.tax_rate || 0);
                      const hasSectionTax = includeTax && taxRate > 0 && sectionTaxableSum > 0;
                      const sectionTax = hasSectionTax ? sectionTaxableSum * taxRate / 100 : 0;
                      const sectionTotalWithTax = sectionSum + sectionTax;
                      return (
                        <React.Fragment key={index}>
                          {item.showSubtotal && sectionSum > 0 && (
                            <>
                              {hasSectionTax && (
                                <>
                                  <tr className="item-section-subtotal-row">
                                    <td colSpan={4} className="section-subtotal-label">Section Subtotal</td>
                                    <td className="total-cell section-subtotal-value">${sectionSum.toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                  <tr className="item-section-tax-row">
                                    <td colSpan={4} className="section-subtotal-label">Section Tax ({taxRate}%)</td>
                                    <td className="total-cell section-subtotal-value">${sectionTax.toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                </>
                              )}
                              <tr className="item-section-subtotal-row">
                                <td colSpan={4} className="section-subtotal-label">Section Total</td>
                                <td className="total-cell section-subtotal-value">${sectionTotalWithTax.toFixed(2)}</td>
                                <td></td>
                              </tr>
                            </>
                          )}
                          <tr className="item-separator-row">
                            <td colSpan={6} className="item-separator">
                              <div className="separator-line"></div>
                              <button
                                type="button"
                                className={`btn-subtotal-toggle ${item.showSubtotal ? 'active' : ''}`}
                                onClick={() => toggleSeparatorSubtotal(index)}
                                title={item.showSubtotal ? 'Hide section subtotal' : 'Show section subtotal'}
                              >
                                Subtotal
                              </button>
                              <button
                                type="button"
                                className="btn-remove-item"
                                onClick={() => removeItem(index)}
                                style={{ marginLeft: '1rem' }}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    }
                    return (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            placeholder="Item description"
                            required
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity || ''}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            required
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price || ''}
                            onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                            required
                            autoComplete="off"
                          />
                        </td>
                        <td className="taxable-cell">
                          <input
                            type="checkbox"
                            checked={item.taxable !== false}
                            onChange={(e) => updateItem(index, 'taxable', e.target.checked)}
                          />
                        </td>
                        <td className="total-cell">
                          ${parseFloat(item.total || 0).toFixed(2)}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <button
                              type="button"
                              className="btn-add-separator"
                              onClick={() => addSeparator(index)}
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            >
                              Add Line
                            </button>
                            {invoiceData.items.length > 1 && (
                              <button
                                type="button"
                                className="btn-remove-item"
                                onClick={() => removeItem(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="invoice-totals">
            <div className="totals-row">
              <span className="totals-label">Subtotal:</span>
              <span className="totals-value">${parseFloat(invoiceData.subtotal || 0).toFixed(2)}</span>
            </div>
            {parseFloat(invoiceData.taxable_subtotal || 0) > 0 && (
              <div className="totals-row">
                <span className="totals-label">Taxable Amount:</span>
                <span className="totals-value">${parseFloat(invoiceData.taxable_subtotal || 0).toFixed(2)}</span>
              </div>
            )}
            {parseFloat(invoiceData.non_taxable_subtotal || 0) > 0 && (
              <div className="totals-row">
                <span className="totals-label">Non-Taxable Amount:</span>
                <span className="totals-value">${parseFloat(invoiceData.non_taxable_subtotal || 0).toFixed(2)}</span>
              </div>
            )}
            {invoiceData.include_tax !== false && (
              <div className="totals-row">
                <div className="tax-input-group">
                  <label>Tax Rate (%):</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={invoiceData.tax_rate}
                    onChange={handleTaxRateChange}
                    className="tax-input"
                  />
                </div>
                <span className="totals-value">${parseFloat(invoiceData.tax_amount || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="totals-row total-row">
              <span className="totals-label">Total:</span>
              <span className="totals-value">${parseFloat(invoiceData.total || 0).toFixed(2)}</span>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea
                rows="4"
                value={invoiceData.notes}
                onChange={(e) => {
                  hasChangesRef.current = true;
                  setInvoiceData(prev => ({ ...prev, notes: e.target.value }));
                }}
                placeholder="Additional notes or terms..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={invoiceData.include_tax !== false}
                  onChange={(e) => {
                    hasChangesRef.current = true;
                    const includeTax = e.target.checked;
                    setInvoiceData(prev => {
                      const taxAmount = includeTax ? parseFloat(prev.taxable_subtotal || 0) * (parseFloat(prev.tax_rate) || 0) / 100 : 0;
                      const total = parseFloat(prev.subtotal || 0) + taxAmount;
                      return {
                        ...prev,
                        include_tax: includeTax,
                        tax_amount: taxAmount.toFixed(2),
                        total: total.toFixed(2),
                      };
                    });
                  }}
                />
                <span>Include tax in total</span>
              </label>
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={invoiceData.hide_totals || false}
                  onChange={(e) => {
                    hasChangesRef.current = true;
                    setInvoiceData(prev => ({ ...prev, hide_totals: e.target.checked }));
                  }}
                />
                <span>Hide totals (for option selection - client can choose which option they prefer)</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <div className="action-buttons-left">
              <button
                type="button"
                className="btn-secondary"
                onClick={handlePrint}
                disabled={!invoiceData.invoice_number}
              >
                Print
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleEmail}
                disabled={!invoiceData.invoice_number || !client.email}
              >
                Email
              </button>
            </div>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : `Create ${invoiceData.type === 'estimate' ? 'Estimate' : 'Invoice'}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
